/**
 * Agent roster + load-balancing panel for the Omnichannel Queue page.
 *
 * Answers three questions the queue list alone cannot: who is routable,
 * how many tickets each agent already holds, and which agent the queue
 * strategy will hand the next interaction to in each campaign.
 *
 * Data comes from GET /omnichannel-queue/workload, which runs the server's
 * real eligibility filter + strategy ranking (OmnichannelRouter.snapshot).
 */
import { useState, type JSX } from 'react'
import {
  Users,
  Info,
  ChevronDown,
  ChevronRight,
  Timer,
  Ticket,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react'

export type OmniStrategyKey =
  | 'fifo'
  | 'round_robin'
  | 'least_open'
  | 'least_today'
  | 'load_balanced'
  | 'sticky'
  | 'random'
  | 'manual'

export interface OmniStrategyInfo {
  key: OmniStrategyKey
  label: string
  description: string
}

export interface OmniQueueSettings {
  strategy: OmniStrategyKey
  agentScope: 'ready' | 'logged_in'
  maxOpenPerAgent: number
  updatedAt: string | null
}

export type IneligibleReason = 'offline' | 'away' | 'not_ready' | 'at_capacity'

export interface WorkloadAgent {
  agentId: string
  userId: string
  name: string
  extension: string | null
  state: string
  liveStatus: string | null
  presence: 'available' | 'busy' | 'away' | 'offline'
  routable: boolean
  ineligibleReason: IneligibleReason | null
  waitingSeconds: number | null
  openTickets: number
  assignedToday: number
  maxLoad: number
  lastAssignedAt: string | null
  campaignKeys: string[]
  campaignNames: string[]
  nextUpForKeys: string[]
}

export interface WorkloadCampaign {
  key: string
  campaignId: string | null
  teamId: string | null
  name: string
  memberAgentIds: string[]
  eligibleCount: number
  pendingCount: number
  nextUpAgentId: string | null
  nextUpAgentName: string | null
}

export interface WorkloadResponse {
  settings: OmniQueueSettings
  strategy: {
    key: OmniStrategyKey
    label: string
    description: string
    steps: string[]
  }
  strategies: OmniStrategyInfo[]
  agents: WorkloadAgent[]
  campaigns: WorkloadCampaign[]
  summary: {
    totalAgents: number
    availableAgents: number
    busyAgents: number
    offlineAgents: number
    routableAgents: number
    openTickets: number
    unroutablePending: number
  }
}

const PRESENCE_STYLE: Record<
  WorkloadAgent['presence'],
  { dot: string; label: string; chip: string }
> = {
  available: { dot: 'bg-green-500', label: 'Ready', chip: 'bg-green-50 text-green-700' },
  busy: { dot: 'bg-amber-500', label: 'Logged in', chip: 'bg-amber-50 text-amber-700' },
  away: { dot: 'bg-orange-400', label: 'On break', chip: 'bg-orange-50 text-orange-700' },
  offline: { dot: 'bg-slate-300', label: 'Offline', chip: 'bg-slate-100 text-slate-500' },
}

const INELIGIBLE_LABEL: Record<IneligibleReason, string> = {
  offline: 'Not logged in',
  away: 'On break / paused',
  not_ready: 'Not Ready',
  at_capacity: 'At ticket cap',
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase()
}

export function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—'
  if (seconds < 60) return `${seconds}s`
  const min = Math.floor(seconds / 60)
  if (min < 60) return `${min}m`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ${min % 60}m`
  return `${Math.floor(hr / 24)}d`
}

export default function OmnichannelWorkloadPanel({
  data,
  loading,
  error,
}: {
  data: WorkloadResponse | null
  loading: boolean
  error: string | null
}): JSX.Element {
  const [showStrategy, setShowStrategy] = useState(false)
  const [showOffline, setShowOffline] = useState(false)

  if (loading && !data) {
    return (
      <div className="space-y-3 rounded-[3px] border border-slate-200 bg-white p-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="animate-pulse space-y-2">
            <div className="h-3 w-1/2 rounded-[3px] bg-slate-100" />
            <div className="h-2 w-full rounded-[3px] bg-slate-100" />
          </div>
        ))}
      </div>
    )
  }

  if (error && !data) {
    return (
      <div className="flex items-start gap-2 rounded-[3px] border border-red-200 bg-red-50 px-4 py-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
        <span className="text-xs text-red-700">{error}</span>
      </div>
    )
  }

  if (!data) return <></>

  const visibleAgents = (showOffline ? data.agents : data.agents.filter((a) => a.presence !== 'offline')).filter(
    (a) => a.campaignKeys.length > 0 || showOffline,
  )
  const maxLoad = Math.max(1, ...data.agents.map((a) => a.maxLoad || a.openTickets))
  const isManual = data.settings.strategy === 'manual'

  return (
    <div className="space-y-4">
      {/* ---- Header + fleet summary ---- */}
      <div className="rounded-[3px] border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-indigo-600" />
            <h2 className="text-sm font-bold text-slate-900">Agents &amp; Load Balancing</h2>
          </div>
          <span className="text-xs text-slate-400">{data.summary.totalAgents} agents</span>
        </div>

        <div className="grid grid-cols-4 divide-x divide-slate-100">
          {[
            { label: 'Routable', value: data.summary.routableAgents, color: 'text-green-600' },
            { label: 'Busy/break', value: data.summary.busyAgents, color: 'text-amber-600' },
            { label: 'Offline', value: data.summary.offlineAgents, color: 'text-slate-400' },
            { label: 'Open', value: data.summary.openTickets, color: 'text-indigo-600' },
          ].map((stat) => (
            <div key={stat.label} className="px-3 py-2.5 text-center">
              <div className={`text-lg font-bold leading-none ${stat.color}`}>{stat.value}</div>
              <div className="mt-1 text-[10px] uppercase tracking-wide text-slate-400">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* ---- Strategy explainer ---- */}
        <button
          type="button"
          onClick={() => setShowStrategy((v) => !v)}
          className="flex w-full items-center gap-2 border-t border-slate-100 px-4 py-2.5 text-left transition hover:bg-slate-50"
        >
          <Info className="h-3.5 w-3.5 flex-shrink-0 text-indigo-500" />
          <span className="flex-1 text-xs font-semibold text-slate-700">
            Routing: {data.strategy.label}
          </span>
          {showStrategy ? (
            <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
          )}
        </button>
        {showStrategy && (
          <div className="border-t border-slate-100 bg-indigo-50/40 px-4 py-3">
            <p className="text-xs leading-relaxed text-slate-600">{data.strategy.description}</p>
            <ol className="mt-2.5 space-y-1.5">
              {data.strategy.steps.map((step, i) => (
                <li key={step} className="flex gap-2 text-xs text-slate-600">
                  <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-[3px] bg-indigo-600 text-[10px] font-bold text-white">
                    {i + 1}
                  </span>
                  <span className="leading-4">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      {/* ---- Unroutable warning ---- */}
      {data.summary.unroutablePending > 0 && (
        <div className="flex items-start gap-2 rounded-[3px] border border-amber-200 bg-amber-50 px-3 py-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
          <span className="text-xs leading-relaxed text-amber-800">
            <strong>{data.summary.unroutablePending}</strong> pending interaction
            {data.summary.unroutablePending === 1 ? ' has' : 's have'} no campaign or team — auto-assign
            cannot route {data.summary.unroutablePending === 1 ? 'it' : 'them'}. Claim manually.
          </span>
        </div>
      )}

      {/* ---- Per-campaign next pick ---- */}
      {data.campaigns.length > 0 && (
        <div className="rounded-[3px] border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-2.5">
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Next assignment by campaign
            </h3>
          </div>
          <div className="divide-y divide-slate-100">
            {data.campaigns.map((c) => (
              <div key={c.key} className="px-4 py-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-xs font-semibold text-slate-900">{c.name}</span>
                  <span className="flex-shrink-0 text-[10px] text-slate-400">
                    {c.eligibleCount}/{c.memberAgentIds.length} routable
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-[3px] bg-orange-50 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700">
                    {c.pendingCount} waiting
                  </span>
                  <ArrowRight className="h-3 w-3 flex-shrink-0 text-slate-300" />
                  {isManual ? (
                    <span className="rounded-[3px] bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">
                      Manual claim
                    </span>
                  ) : c.nextUpAgentName ? (
                    <span className="inline-flex min-w-0 items-center gap-1.5 rounded-[3px] bg-green-50 px-1.5 py-0.5 text-[10px] font-semibold text-green-700">
                      <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-green-500" />
                      <span className="truncate">{c.nextUpAgentName}</span>
                    </span>
                  ) : (
                    <span className="rounded-[3px] bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">
                      {c.memberAgentIds.length === 0 ? 'No campaign members' : 'No logged-in agent'}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---- Agent roster with load bars ---- */}
      <div className="rounded-[3px] border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
          <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">
            Agent workload
          </h3>
          <button
            type="button"
            onClick={() => setShowOffline((v) => !v)}
            className="text-[10px] font-medium text-indigo-600 hover:text-indigo-700"
          >
            {showOffline ? 'Hide offline' : `Show all (${data.summary.offlineAgents} offline)`}
          </button>
        </div>

        {visibleAgents.length === 0 ? (
          <div className="px-4 py-8 text-center text-xs text-slate-400">
            No campaign agents logged in. Auto-assign has nobody to route to.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {visibleAgents.map((agent) => {
              const style = PRESENCE_STYLE[agent.presence]
              const isNextUp = !isManual && agent.nextUpForKeys.length > 0
              const loadPct = Math.round((agent.openTickets / maxLoad) * 100)

              return (
                <div
                  key={agent.agentId}
                  className={`px-4 py-3 transition ${isNextUp ? 'bg-green-50/40' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="relative flex-shrink-0">
                      <div className="flex h-8 w-8 items-center justify-center rounded-[3px] bg-indigo-100 text-[11px] font-bold text-indigo-700">
                        {initials(agent.name)}
                      </div>
                      <span
                        className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white ${style.dot}`}
                        title={style.label}
                      />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-xs font-semibold text-slate-900">
                          {agent.name}
                        </span>
                        {agent.extension && (
                          <span className="flex-shrink-0 text-[10px] text-slate-400">
                            ext {agent.extension}
                          </span>
                        )}
                        {isNextUp && (
                          <span className="flex-shrink-0 rounded-[3px] bg-green-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                            Next up
                          </span>
                        )}
                      </div>

                      <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1">
                        <span
                          className={`inline-flex items-center rounded-[3px] px-1.5 py-0.5 text-[10px] font-medium ${style.chip}`}
                        >
                          {agent.liveStatus ?? style.label}
                        </span>
                        {agent.ineligibleReason && (
                          <span className="inline-flex items-center rounded-[3px] bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                            {INELIGIBLE_LABEL[agent.ineligibleReason]}
                          </span>
                        )}
                        <span
                          className="inline-flex items-center gap-1 text-[10px] text-slate-400"
                          title="Waiting since the last status change or last assignment — the FIFO key"
                        >
                          <Timer className="h-3 w-3" />
                          {formatDuration(agent.waitingSeconds)}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600">
                          <Ticket className="h-3 w-3 text-slate-400" />
                          {agent.openTickets}{agent.maxLoad > 0 ? `/${agent.maxLoad}` : ''} open
                          <span className="font-normal text-slate-400">· {agent.assignedToday} today</span>
                        </span>
                      </div>

                      {/* Load bar — relative to the busiest agent */}
                      <div className="mt-2 h-1 w-full overflow-hidden rounded-[3px] bg-slate-100">
                        <div
                          className={`h-full rounded-[3px] transition-all ${
                            agent.presence === 'offline'
                              ? 'bg-slate-300'
                              : loadPct >= 80
                                ? 'bg-red-500'
                                : loadPct >= 50
                                  ? 'bg-amber-500'
                                  : 'bg-indigo-500'
                          }`}
                          style={{ width: `${Math.max(loadPct, agent.openTickets > 0 ? 6 : 0)}%` }}
                        />
                      </div>

                      {agent.campaignNames.length > 0 && (
                        <div className="mt-2 flex flex-wrap items-center gap-1">
                          {agent.campaignNames.map((name) => (
                            <span
                              key={name}
                              className="rounded-[3px] border border-indigo-100 bg-indigo-50 px-1.5 py-0.5 text-[9px] font-medium text-indigo-600"
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
